import { Avatar, AvatarFallback, AvatarImage } from '@crowdfpl/ui-web/avatar';
import { Card, CardContent } from '@crowdfpl/ui-web/card';
import { Skeleton } from '@crowdfpl/ui-web/skeleton';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { UsernameWithXLink } from '@/components/shared/UsernameWithXLink';
import { trackUmamiEvent } from '@/helpers/analytics';
import { useIsMobile } from '@/hooks/utils/use-mobile';
import type { TopClimberEntry } from '@/types/gameweekLeaderboard';

interface TopManagersSectionProps {
  gameweekNumber: number;
  topClimbers: TopClimberEntry[];
  loading: boolean;
}

const medal = (rank: number) => (rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : '');

export function TopManagersSection({ gameweekNumber, topClimbers, loading }: TopManagersSectionProps) {
  const isMobile = useIsMobile();

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-8 w-20" />
        </div>
        {/* Mobile skeleton */}
        <div className="md:hidden flex justify-center gap-2 px-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 w-[110px] rounded-lg" />
          ))}
        </div>
        {/* Tablet skeleton */}
        <div className="hidden md:flex lg:hidden justify-center gap-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-44 w-[160px] rounded-lg" />
          ))}
        </div>
        {/* Desktop skeleton */}
        <div className="hidden lg:grid grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (topClimbers.length === 0) return null;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <h2 className="mb-3 text-center text-lg font-bold text-foreground md:mb-4 md:text-left md:text-xl">Top-3 Managers GW{gameweekNumber}</h2>
        <Link
          to={`/leaderboard?gw=${gameweekNumber}`}
          onClick={() => trackUmamiEvent('home_top_climbers_view_all', { gameweek: gameweekNumber })}
          className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground ${isMobile ? 'h-8 px-3 text-xs' : 'h-10 px-4 text-sm'}`}
        >
          View all
          <ArrowRight className="h-3 w-3 md:h-4 md:w-4" />
        </Link>
      </div>

      {/* Desktop Cards - Horizontal compact layout (lg+) */}
      <div className="hidden lg:grid grid-cols-3 gap-4">
        {topClimbers.map((entry) => (
          <Link
            key={entry.user_id}
            to={`/profile/${entry.username}`}
            onClick={() => trackUmamiEvent('home_top_climber_click', { username: entry.username, gameweek: gameweekNumber })}
          >
            <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
              <CardContent className="p-4 flex items-center gap-4">
                {/* Medal */}
                <div className="text-3xl flex-shrink-0">{medal(entry.rank)}</div>

                {/* Avatar */}
                <Avatar className="h-12 w-12 flex-shrink-0">
                  <AvatarImage src={entry.avatar_url || undefined} />
                  <AvatarFallback className="bg-primary/10 text-primary text-sm">
                    {entry.username.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm min-w-0 overflow-hidden">
                    <UsernameWithXLink
                      username={entry.username}
                      twitterUsername={entry.twitter_username}
                      showTwitterUsername={entry.show_twitter_username}
                      iconClassName="h-3 w-3"
                      insideLink
                      trackSource="home_top_managers"
                    />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {entry.votes_in_gw} votes • {entry.success_rate_gw.toFixed(0)}% acc
                  </div>
                </div>

                {/* Rating Change */}
                <div className="text-right flex-shrink-0">
                  <div className="text-lg font-bold text-success">+{entry.rating_change}</div>
                  <div className="text-[10px] text-muted-foreground">rating</div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Tablet Cards - Semi-compact vertical layout (md to lg) */}
      <div className="hidden md:flex lg:hidden justify-center gap-3">
        {topClimbers.map((entry) => (
          <Link
            key={entry.user_id}
            to={`/profile/${entry.username}`}
            onClick={() => trackUmamiEvent('home_top_climber_click', { username: entry.username, gameweek: gameweekNumber })}
            className="flex-1 max-w-[160px]"
          >
            <Card className="text-center cursor-pointer h-full hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="text-2xl mb-2">{medal(entry.rank)}</div>
                <Avatar className="h-12 w-12 mx-auto mb-2">
                  <AvatarImage src={entry.avatar_url || undefined} />
                  <AvatarFallback className="bg-primary/10 text-primary text-sm">
                    {entry.username.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="font-semibold text-sm mb-1 min-w-0 overflow-hidden w-full flex justify-center">
                  <span className="min-w-0 max-w-full overflow-hidden block">
                    <UsernameWithXLink
                      username={entry.username}
                      twitterUsername={entry.twitter_username}
                      showTwitterUsername={entry.show_twitter_username}
                      iconClassName="h-3 w-3"
                      insideLink
                      trackSource="home_top_managers"
                      className="max-w-full"
                    />
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mb-2">
                  {entry.votes_in_gw} votes • {entry.success_rate_gw.toFixed(0)}%
                </div>
                <div className="text-lg font-bold text-success">+{entry.rating_change}</div>
                <div className="text-[10px] text-muted-foreground">rating</div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden flex justify-center gap-2 px-2">
        {topClimbers.map((entry) => (
          <Link
            key={entry.user_id}
            to={`/profile/${entry.username}`}
            onClick={() => trackUmamiEvent('home_top_climber_click', { username: entry.username, gameweek: gameweekNumber })}
            className="flex-1 max-w-[110px]"
          >
            <Card className="text-center cursor-pointer h-full">
              <CardContent className="p-3">
                <div className="text-2xl mb-2">{medal(entry.rank)}</div>
                <Avatar className="h-10 w-10 mx-auto mb-2">
                  <AvatarImage src={entry.avatar_url || undefined} />
                  <AvatarFallback className="bg-primary/10 text-primary text-xs">
                    {entry.username.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="font-bold text-xs mb-1 px-1 min-w-0 overflow-hidden w-full flex justify-center">
                  <span className="min-w-0 max-w-full overflow-hidden block">
                    <UsernameWithXLink
                      username={entry.username}
                      twitterUsername={entry.twitter_username}
                      showTwitterUsername={entry.show_twitter_username}
                      iconClassName="h-2.5 w-2.5"
                      insideLink
                      trackSource="home_top_managers"
                      className="max-w-full"
                    />
                  </span>
                </div>
                <div className="text-sm font-bold text-success">+{entry.rating_change}</div>
                <div className="text-[10px] text-muted-foreground">rating</div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
